import { useEffect, useState, useRef } from "react";
import { Button } from "../ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import api from "../../lib/api";
import { Input } from "../ui/input";
import { CheckIcon, XIcon, UploadIcon, UploadCloud } from "lucide-react";
import { Trash2 } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { useAuth } from "../../lib/authContext";
import Pagination from "../ui/pagination";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "../ui/select";
import { CustomSelect } from "../ui/custom-select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import * as XLSX from "xlsx";

export default function UnmappedMeds({ entities }: { entities: any }) {
  const [unmappedMeds, setUnmappedMeds] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [masterMeds, setMasterMeds] = useState([]);
  const [selectedMed, setSelectedMed] = useState<any>(null);
  const [legacyMed, setLegacyMed] = useState<any>(null);
  const [total, setTotal] = useState(0);
  const [selectedEntity, setSelectedEntity] = useState<any>(0);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadEntity, setUploadEntity] = useState<any>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [filePreview, setFilePreview] = useState<any[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { user, excelPermissions } = useAuth();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const getUnmappedMeds = () => {
    if (!user?.id) return;
    api.getMappedMeds(page, limit, "unmapped", selectedEntity, user.id).then(data => {
      if (!data || data.totalCount === 0) {
        setUnmappedMeds([]);
        setTotal(0);
        toast({
          title: "No unmapped medications found",
          description: "Please try again in sometime or reload once",
        });
      } else {
        setUnmappedMeds(data?.data || []);
        setTotal(data?.totalCount || 0);
      }
    }).catch(err => {
      console.error("Failed to fetch unmapped meds:", err);
      setUnmappedMeds([]);
      setTotal(0);
    });
  };

  useEffect(() => {
    getUnmappedMeds();
  }, [page, limit, selectedEntity, user?.id]);

  useEffect(() => {
    if (searchTerm && selectedMed?.id && user?.id) {
      api
        .searchMasterMeds(searchTerm, selectedMed?.id, selectedMed?.entity_id, user.id)
        .then(data => {
          setMasterMeds(data?.data || []);
        }).catch(err => {
          console.error("Failed to search master meds:", err);
          setMasterMeds([]);
        });
    }
  }, [searchTerm, selectedMed]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setSelectedMed(null);
        setSearchTerm("");
        setLegacyMed(null);
      }
    }

    if (selectedMed) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedMed]);

  const handleSearch = (searchTerm: string, med: any) => {
    setSearchTerm(searchTerm);
    if (searchTerm?.length > 2) {
      setSelectedMed(med);
    }
  };

  const handleMapMed = (med: any, masterMed: any) => {
    setLegacyMed(null);
    setSelectedMed(null);
    setSearchTerm("");
    api.mapMed(med.id, masterMed.id, user?.id || "").then(data => {
      if (data.success) {
        toast({
          title: `Medicine ${med.product_name} mapped successfully to ${masterMed.drugname}`,
        });
        getUnmappedMeds();
      } else {
        toast({
          title: `Failed to map the medicine ${med.product_name} to ${masterMed.drugname}`,
        });
      }
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (
        file.type !==
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ) {
        toast({
          title: "Invalid file format",
          description: "Please upload only .xlsx files",
          variant: "destructive",
        });
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }
      setSelectedFile(file);
      // Reset preview when a new file is selected
      setFilePreview([]);
      setShowPreview(false);
    }
  };

  const generatePreview = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      // Parse Excel file client-side
      const reader = new FileReader();
      reader.onload = e => {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // Convert to JSON
        const jsonData: any = XLSX.utils.sheet_to_json(worksheet);

        const requiredFields = ["ProductID", "ProductName", "MRP", "Packing"];
        const missingFields = requiredFields.filter(
          field =>
            jsonData[0][field] === undefined ||
            jsonData[0][field] === "" ||
            jsonData[0][field] === null,
        );
        if (missingFields.length > 0) {
          toast({
            title: "Missing required fields",
            description: `Please ensure the following fields are present: ${missingFields.join(
              ", ",
            )}`,
          });
          setIsUploading(false);
          return;
        }
        // Limit to first 10 rows for preview
        const previewData = jsonData.slice(0, 10);
        setFilePreview(previewData);
        setShowPreview(true);
        setIsUploading(false);
      };

      reader.readAsBinaryString(selectedFile);
    } catch (error) {
      toast({
        title: "Preview failed",
        description: "An error occurred while generating preview",
        variant: "destructive",
      });
      setIsUploading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploadEntity) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("entity_id", uploadEntity.toString());
      if (user?.id) {
        formData.append("user_id", user.id);
      }

      const response = await api.uploadMedsExcel(formData);

      toast({
        title: "Excel file uploaded successfully",
        description:
          response.message || `${response.count || 0} medications added`,
      });
      getUnmappedMeds();
      setIsUploadDialogOpen(false);
      setSelectedEntity(uploadEntity);
      getUnmappedMeds();
      setSelectedFile(null);
      setUploadEntity(null);
      setShowPreview(false);
      setFilePreview([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      toast({
        title: "Upload failed",
        description: "An error occurred while uploading the file",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const resetUploadForm = () => {
    setSelectedFile(null);
    setUploadEntity(null);
    setShowPreview(false);
    setFilePreview([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const confirmDelete = (med: any) => {
    setDeleteTarget(med);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const resp = await api.deletePharmacyProduct(deleteTarget.id);
      if (resp?.success) {
        setUnmappedMeds(prev => prev.filter((m: any) => m.id !== deleteTarget.id));
        setTotal(prev => Math.max(0, prev - 1));
        toast({ title: `Deleted ${deleteTarget.product_name}` });
      } else {
        toast({ title: resp?.message || "Failed to delete product", variant: "destructive" });
      }
    } catch (e) {
      toast({ title: "Failed to delete product", variant: "destructive" });
    } finally {
      setIsDeleteDialogOpen(false);
      setDeleteTarget(null);
    }
  };

  // Enhanced dropdown position function that considers proximity to pagination
  const getDropdownPosition = (element: HTMLElement | null) => {
    if (!element) return "bottom";

    const rect = element.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    // Find the pagination component
    const paginationElement = document.querySelector(
      ".flex.justify-between.items-center.mt-4",
    );
    const tableContainer = document.querySelector(".max-h-\\[600px\\]");

    if (paginationElement && tableContainer) {
      const paginationRect = paginationElement.getBoundingClientRect();
      const tableRect = tableContainer.getBoundingClientRect();

      // Calculate distance to pagination and table boundaries
      const distanceFromTableBottom = tableRect.bottom - rect.bottom;
      const distanceFromTableTop = rect.top - tableRect.top;
      const distanceToPagination = paginationRect.top - rect.bottom;

      // If close to pagination (within 150px) or near table bottom
      if (
        distanceToPagination < 150 ||
        distanceFromTableBottom < 200 ||
        spaceBelow < 200
      ) {
        // Before showing on top, make sure there's enough space
        if (distanceFromTableTop > 200 && spaceAbove > 200) {
          return "top";
        }
      }
    }

    return "bottom";
  };

  return (
    <div className="mt-4">
      <div className="flex justify-between items-center mb-4">
        <div className="w-1/3">
          <CustomSelect
            options={entities}
            value={selectedEntity}
            onChange={setSelectedEntity}
          />
        </div>
        <Dialog
          open={isUploadDialogOpen}
          onOpenChange={open => {
            setIsUploadDialogOpen(open);
            if (!open) resetUploadForm();
          }}
        >
          {excelPermissions.canUploadExcel && (
            <DialogTrigger asChild>
              <Button onClick={() => setIsUploadDialogOpen(true)}>
                <UploadIcon className="mr-2 h-4 w-4" /> Upload Excel
              </Button>
            </DialogTrigger>
          )}
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Upload Medications Excel</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <label htmlFor="entity">Select Pharmacy</label>
                <CustomSelect
                  options={entities.filter((entity: any) => entity.value !== 0)}
                  value={uploadEntity}
                  onChange={setUploadEntity}
                  placeholder="Select pharmacy"
                />
              </div>
              <div className="grid gap-2">
                <label htmlFor="file">Upload Excel File</label>
                <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-500 rounded-md h-60 cursor-pointer hover:bg-gray-900 transition"
                onClick={() => fileInputRef.current?.click()}>

                <UploadCloud className="h-10 w-10 text-gray-400 mb-2" />
                <p className="text-sm text-gray-300">
                  <span className="text-blue-400 underline">Browse</span> or drag a file here
                </p>

                <Input
                  id="file"
                  type="file"
                  ref={fileInputRef}
                  accept=".xlsx"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <p  className="text-xs text-gray-500 mt-2 text-center max-w-xs">
                  Upload Excel file (.xlsx) with medication data | Required
                  fields: ProductID, ProductName, MRP, Packing
                </p>
                </div>
              </div>

              {showPreview && filePreview.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-lg font-medium mb-2">File Preview</h3>
                  <div className="max-h-60 overflow-y-auto border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {Object.keys(filePreview[0]).map(header => (
                            <TableHead key={header}>{header}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filePreview.map((row: any) => (
                          <TableRow key={row.id}>
                            {Object.values(row).map((cell: any) => (
                              <TableCell key={cell}>{cell}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Showing {filePreview.length} rows from the file
                  </p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              {!showPreview ? (
                <Button
                  onClick={generatePreview}
                  disabled={!selectedFile || !uploadEntity || isUploading}
                >
                  {isUploading ? "Processing..." : "Preview Data"}
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowPreview(false);
                      setFilePreview([]);
                    }}
                  >
                    Back
                  </Button>
                  <Button onClick={handleUpload} disabled={isUploading}>
                    {isUploading ? "Uploading..." : "Confirm Upload"}
                  </Button>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="mt-4 border rounded-md dark:border-gray-700 max-h-[600px] overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product ID</TableHead>
              <TableHead>Product Name</TableHead>
              <TableHead>MRP</TableHead>
              <TableHead>Packing</TableHead>
              <TableHead>Manufacturer</TableHead>
              <TableHead>Pharmacy Name</TableHead>
              <TableHead>Map to Master</TableHead>
              <TableHead>Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {unmappedMeds?.length > 0 ? (
              unmappedMeds.map((med: any) => (
                <TableRow key={med?.id || Math.random()}>
                  <TableCell>{med.product_id}</TableCell>
                  <TableCell>{med.product_name}</TableCell>
                  <TableCell>₹{med.mrp}</TableCell>
                  <TableCell>{med.product_packing}</TableCell>
                  <TableCell>{med.product_manufacturer}</TableCell>
                  <TableCell>{med.entity_name}</TableCell>
                  <TableCell>
                    {legacyMed?.id && selectedMed?.id === med.id ? (
                      legacyMed.drugname
                    ) : (
                      <div className="relative">
                        <Input
                          id={`med-input-${med.id}`}
                          type="text"
                          placeholder="Search Master Product"
                          className="focus:ring-0 focus:ring-offset-0"
                          onClick={e => {
                            setSelectedMed(med);
                            const searchStr = med?.product_name ? med.product_name.split(" ")[0] : "";
                            handleSearch(searchStr, med);
                          }}
                          onChange={e => handleSearch(e.target.value, med)}
                          value={
                            searchTerm && selectedMed?.id === med?.id
                              ? searchTerm
                              : ""
                          }
                        />

                        {searchTerm && selectedMed?.id === med.id && (
                          <div
                            ref={dropdownRef}
                            className={`absolute ${
                              getDropdownPosition(
                                document.getElementById(`med-input-${med.id}`),
                              ) === "top"
                                ? "bottom-full mb-1"
                                : "top-full mt-1"
                            } z-50 w-full bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 shadow-lg overflow-hidden`}
                          >
                            <div className="max-h-[200px] overflow-y-auto">
                              {masterMeds?.length === 0 ? (
                                <div className="px-4 py-2 text-sm text-gray-500">
                                  No results found.
                                </div>
                              ) : (
                                <div>
                                  {masterMeds?.map((masterMed: any) => (
                                    <div
                                      key={masterMed?.id || Math.random()}
                                      className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                                      onClick={() => {
                                        setLegacyMed(masterMed);
                                      }}
                                      onKeyUp={() => {
                                        setLegacyMed(masterMed);
                                      }}
                                      onKeyDown={() => {
                                        setLegacyMed(masterMed);
                                      }}
                                    >
                                      {masterMed.drugname} (
                                      {masterMed.drugquantity}
                                      {masterMed.drugmeasure}){" "}
                                      {masterMed.packform} - ₹{masterMed.mrp}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2 items-center">
                      {legacyMed?.id && selectedMed?.id === med.id && (
                        <>
                          <Button variant="outline" size="sm">
                            <CheckIcon
                              className="w-4 h-4"
                              onClick={() => {
                                handleMapMed(med, legacyMed);
                              }}
                            />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setLegacyMed(null);
                              setSelectedMed(null);
                              setSearchTerm("");
                            }}
                          >
                            <XIcon className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => confirmDelete(med)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} className="text-center">
                  No unmapped medications found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete product?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Are you sure you want to delete
              {" "}
              <span className="font-semibold">{deleteTarget?.product_name}</span>
              ? This action cannot be undone.
            </p>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="flex justify-between items-center mt-4">
        <div className="w-1/6">
          <Select
            value={limit.toString()}
            onValueChange={value => setLimit(Number(value))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select limit" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-gray-500 mt-2">items per page</span>
        </div>
        <Pagination
          page={page}
          totalPages={Math.ceil(total / limit)}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
}
